import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, ChevronRight, CheckCircle } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { locationPages, serviceDetails } from '../mockData';

const SITE_URL = 'https://www.hexalogixautomation.com';

export const LocationServicePage = () => {
  const { locationSlug } = useParams();
  const page = locationPages[locationSlug];

  if (!page) {
    return <Navigate to="/services" replace />;
  }

  const relatedService = serviceDetails[page.serviceSlug];
  const pageUrl = `${SITE_URL}/locations/${locationSlug}`;

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: page.title,
      description: page.seoDescription,
      provider: {
        '@type': 'LocalBusiness',
        name: 'Hexalogix Automation',
        url: SITE_URL,
        telephone: '+971-55-916-2533'
      },
      serviceType: page.title,
      areaServed: [page.region, 'UAE', 'Africa', 'Middle East'],
      url: pageUrl
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/services` },
        { '@type': 'ListItem', position: 3, name: page.title, item: pageUrl }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  ];

  return (
    <>
      <SEOHead
        title={page.seoTitle}
        description={page.seoDescription}
        keywords={page.keywords}
        canonical={pageUrl}
        schemas={schemas}
      />

      <div className="min-h-screen">
        <section className="relative pt-16 md:pt-[106px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={page.heroImage}
              alt={page.heroTitle}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E]/90 via-[#0A0F1E]/75 to-[#0A0F1E]/40" />
          </div>
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#0057B8]" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-300">{page.title}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-6">
              {page.heroTitle}
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl leading-relaxed mb-8">
              {page.heroText}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Link to="/contact" className="w-full sm:w-auto">
                <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#0057B8] hover:bg-[#003D82] text-white font-bold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide transition-all duration-200 hover:shadow-xl hover:shadow-[#0057B8]/40 group">
                  Request a Quote
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              {relatedService && (
                <Link to={`/services/${page.serviceSlug}`} className="w-full sm:w-auto">
                  <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide transition-all duration-200 hover:bg-white/10 group">
                    View Main Service Page
                  </button>
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                  <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">
                    {page.introTitle}
                  </span>
                </div>
                {page.introBody.map((paragraph, index) => (
                  <p key={index} className="text-gray-600 leading-relaxed mb-6 text-lg">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="bg-[#F0F4F9] border border-gray-200 p-8">
                <h2 className="text-2xl sm:text-3xl font-black text-[#0A0F1E] mb-6">
                  Why This Page Matters
                </h2>
                <div className="space-y-4">
                  {page.valuePoints.map((point) => (
                    <div key={point} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#0057B8] mt-0.5 flex-shrink-0" />
                      <span className="text-base text-gray-700 leading-relaxed">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] mb-4">{page.marketTitle}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {page.marketPoints.map((item) => (
                <div key={item.title} className="bg-white border border-gray-200 p-8">
                  <h3 className="text-xl font-black text-[#0A0F1E] mb-3">{item.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] mb-4">{page.industryTitle}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {page.industries.map((industry) => (
                <div key={industry} className="text-center p-6 border border-gray-200 hover:border-[#0057B8] transition-colors bg-white">
                  <h3 className="font-bold text-[#0A0F1E] text-base">{industry}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-4">
                {page.faqTitle}
              </h2>
            </div>
            <div className="space-y-6">
              {page.faqs.map((faq) => (
                <div key={faq.question} className="border border-gray-200 bg-white p-6 hover:border-[#0057B8]/30 transition-colors">
                  <h3 className="font-bold text-[#0A0F1E] text-lg mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed text-base">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative bg-[#0057B8] py-20 md:py-24 overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-[#003D82]/40 -skew-x-12 translate-x-16" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-4">
              {page.ctaTitle}
            </h2>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto mb-8">
              {page.ctaText}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full sm:w-auto">
              <Link to="/contact" className="w-full sm:w-auto">
                <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-white text-[#0057B8] hover:bg-gray-100 font-bold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide transition-all duration-200 hover:shadow-xl group">
                  Talk to Hexalogix
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              {relatedService && (
                <Link to={`/services/${page.serviceSlug}`} className="w-full sm:w-auto">
                  <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 border-2 border-white/50 hover:border-white text-white font-semibold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide transition-all duration-200 hover:bg-white/10">
                    View Related Service
                  </button>
                </Link>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
